<script setup lang="ts">
  import { IconImage } from '@/shared/ui';
  import TableBorderPatternAllIcon from '@/shared/assets/icons/TableBorderPatternAllIcon.svg';
  import TableBorderPatternTopIcon from '@/shared/assets/icons/TableBorderPatternTopIcon.svg';
  import TableBorderPatternRightIcon from '@/shared/assets/icons/TableBorderPatternRightIcon.svg';
  import TableBorderPatternBottomIcon from '@/shared/assets/icons/TableBorderPatternBottomIcon.svg';
  import TableBorderPatternLeftIcon from '@/shared/assets/icons/TableBorderPatternLeftIcon.svg';
  import TableBorderPatternTopBottomIcon from '@/shared/assets/icons/TableBorderPatternTopBottomIcon.svg';
  import TableBorderPatternLeftRightIcon from '@/shared/assets/icons/TableBorderPatternLeftRightIcon.svg';
  import TableBorderPatternNoneIcon from '@/shared/assets/icons/TableBorderPatternNoneIcon.svg';
  import { BORDER_PATTERNS, type BorderPattern } from '../../../../../lib/cell-border-utils';

  interface Props {
    modelValue: BorderPattern;
  }

  defineProps<Props>();

  const emit = defineEmits<{
    (e: 'update:modelValue', value: BorderPattern): void;
  }>();

  const patternIcons: Record<BorderPattern, string | object> = {
    all: TableBorderPatternAllIcon,
    top: TableBorderPatternTopIcon,
    right: TableBorderPatternRightIcon,
    bottom: TableBorderPatternBottomIcon,
    left: TableBorderPatternLeftIcon,
    'top-bottom': TableBorderPatternTopBottomIcon,
    'left-right': TableBorderPatternLeftRightIcon,
    none: TableBorderPatternNoneIcon,
  };
</script>

<template>
  <div class="border-pattern-grid">
    <button
      v-for="pattern in BORDER_PATTERNS"
      :key="pattern.value"
      v-tippy="{
        content: pattern.label,
        placement: 'top',
        theme: 'custom',
        trigger: 'mouseenter',
        hideOnClick: true,
      }"
      type="button"
      :class="['border-pattern-btn', modelValue === pattern.value ? 'active' : '']"
      :aria-label="pattern.label"
      @click="emit('update:modelValue', pattern.value)"
    >
      <IconImage
        class="border-pattern-icon"
        :src="patternIcons[pattern.value]"
        :alt="pattern.label"
      />
    </button>
  </div>
</template>

<style scoped>
  .border-pattern-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 2px;
    padding: 8px;
    background-color: var(--color-gray-01);
    border-radius: 0.375rem;
  }

  .border-pattern-btn {
    width: 32px;
    height: 32px;
    border: none;
    background-color: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: background-color 0.15s ease;
    padding: 0;
  }

  .border-pattern-btn:hover {
    background-color: var(--color-gray-01);
  }

  .border-pattern-btn.active {
    background-color: rgba(var(--theme-secondary-rgb, 251, 113, 133), 0.1);
  }

  .border-pattern-icon {
    color: var(--color-gray-07);
  }

  .border-pattern-btn.active .border-pattern-icon {
    color: var(--theme-secondary-ink);
  }

  .border-pattern-icon :deep(svg) {
    width: 20px;
    height: 20px;
    overflow: visible;
  }
</style>
